import { useEffect, useState } from "react";
import { DeleteIcon, EditIcon, ViewIcon } from "../../utils/icon.utils";
import Table from "../../ui/Table/Table";
import { GroupBtn } from "../../ui/GroupBtn";
import {
  extractValues,
  filterValues,
  generateChassis,
  getCarInfoBySlug,
  stockListFormatter,
  useDeleteStockListMutation,
  useGetAllStockListQuery
} from "../../utils/stockList.utils";
import { onConfirm } from "../../utils/sweetAlert.utils";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import { useNavigate } from "react-router-dom";
import { tableDate } from "../../utils/date.utils";
import StockListPrice from "./StockListPrice";
import StockListLocation from "./StockListLocation";
import { deleteImageByChassis } from "../../utils/imageUpload.utils";
import { mileageKm } from "../../utils/number.utils";
import StockListForward from "./StockListForward";
import OffCanvas from "../../ui/OffCanvas";
import StockListFilterForm from "./StockListFilterForm";
import { objectToQueryString } from "../../utils/object.utils";
import StockToolBar from "./StockToolBar";
import { arrayMap } from "../../utils/array.utils";
import { useQueryParams } from "../../utils/queryParams.utils";

export default function StockList() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [imageDeleting, setImageDeleting] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const pageSize = 10;

  const { searchParams } = useQueryParams();

  // filter data
  const { setFilter, getFilter } = filterValues();
  const filteredValues = getFilter();
  const { values } = extractValues(filteredValues);

  const [params, setParams] = useState({
    page: 1,
    size: pageSize,
    // initiall set filter data if have
    ...values
  });

  useEffect(() => {
    setParams((prev) => ({ ...prev, page: currentPage }));
  }, [currentPage]);

  const { isLoading: loading, data } = useGetAllStockListQuery({
    query: `?${objectToQueryString(params)}&${searchParams}`
  });

  const onFilter = (values) => {
    setIsSearched(false);
    setParams((prev) => ({ ...prev, page: 1, ...values }));
  };

  const onSearch = (values) => {
    setFilter(null);
    setParams({ page: 1, size: pageSize, ...values });
    setIsSearched(true);
  };

  const handleSearchClear = () => {
    setParams({ page: 1, size: pageSize });
    setIsSearched(false);
  };

  const onFilterClear = () => {
    setFilter(null);
    setParams({ page: 1, size: pageSize });
  };

  const { loading: deleting, deleteStockList } = useDeleteStockListMutation(["deleteStockList"]);

  const totalPage = data?.last_page === 1 && data?.total === 0 ? 0 : data?.last_page;

  const breadCrumbs = [
    {
      name: "Pages",
      link: "/pages"
    },
    {
      name: "Stock List"
    }
  ];

  const thead = [
    "#",
    "Chassis",
    "Brand",
    "Model",
    "Body Type",
    "Color",
    "Year",
    "Package",
    "Grade",
    "CC",
    "Fuel",
    "Forward",
    "Mileage",
    "Price",
    "Location",
    "Date",
    "Action"
  ];

  const handleOption = (item, name) => {
    return getCarInfoBySlug(name, item)?.name ?? "-";
  };

  const tbody =
    arrayMap(data?.data, (item) => {
      const brand = handleOption(item, "brand");
      const model = handleOption(item, "model");
      const carPackage = handleOption(item, "package");
      const color = handleOption(item, "color");
      const cc = handleOption(item, "cubic-capacity-(cc)");
      const grade = handleOption(item, "auction-grade");
      const bodyType = handleOption(item, "body-type");
      const fuel = handleOption(item, "fuel");
      const chassisPrefix = item?.chassis_code?.name ?? "-";
      const year = handleOption(item, "year");
      const date = item?.created_at ? tableDate(item.created_at) : "-";
      const location = handleOption(item, "car-location");
      const company = handleOption(item, "company");
      const mileage = mileageKm(item?.mileage);

      return {
        "#": item?.sku ?? "-",
        Chassis: (chassisPrefix ?? "") + "-" + (item?.chassis ?? "-"),
        Brand: brand,
        Model: model,
        Color: color,
        Year: year,
        Package: carPackage,
        "Body Type": bodyType,
        Grade: grade,
        CC: cc,
        Fuel: fuel,
        Location: <StockListLocation location={location} date={item?.eta_date} />,
        Date: date,
        Price: <StockListPrice asking={item?.asking_price} sale={item?.discount_price} />,
        Forward: (
          <StockListForward
            company={company === "-" ? "Own" : company}
            price={item?.forward_price}
          />
        ),
        Mileage: mileage,
        ...item
      };
    }) || [];

  const ActionComponent = ({ data }) => {
    const handleEdit = (item) => {
      navigate(`/stock-list/${item?.sku}/edit`);
    };

    const handleView = (item) => {
      navigate(`/stock-list/${item?.sku}/view`);
    };

    const handleDelete = async (item) => {
      const { isConfirmed } = await onConfirm();
      if (isConfirmed) {
        try {
          let formattedData = stockListFormatter(item);
          const fullChassis = generateChassis(formattedData);

          setImageDeleting(true);

          await deleteStockList(item?.id);
          await deleteImageByChassis(fullChassis);

          setImageDeleting(false);
        } catch (error) {
          setImageDeleting(false);
        }
      }
    };

    const btns = [
      {
        icon: DeleteIcon,
        onClick: () => handleDelete(data),
        color: "danger"
      },
      {
        icon: EditIcon,
        onClick: () => handleEdit(data),
        color: "primary"
      },
      {
        icon: ViewIcon,
        onClick: () => handleView(data),
        color: "info"
      }
    ];
    return <GroupBtn btns={btns} justify="start" />;
  };

  const isLoading = loading || deleting || imageDeleting;

  const handleAdd = () => {
    navigate("/stock-list/add");
  };

  const handleFilterToggle = () => {
    setFilterOpen((prev) => !prev);
  };

  return (
    <PageLayout
      title="Stock List"
      subTitle={`Total: ${data?.total ?? 0}`}
      breadCrumbs={breadCrumbs}>
      <StockToolBar
        handleAdd={handleAdd}
        handleFilterToggle={handleFilterToggle}
        filteredValues={filteredValues}
        onFilterClear={onFilterClear}
        onSearch={onSearch}
        isSearched={isSearched}
        handleSearchClear={handleSearchClear}
      />

      <Table
        thead={thead}
        tbody={tbody}
        action={ActionComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={totalPage}
      />
      {<Loading loading={isLoading} />}
      <OffCanvas title="Filter Stock List" toggle={handleFilterToggle} isOpen={filterOpen}>
        <StockListFilterForm onClose={handleFilterToggle} onFilter={onFilter} />
      </OffCanvas>
    </PageLayout>
  );
}
